import React, { CSSProperties, useRef } from 'react';

import type { Cell, HeaderGroup, Row, TableRowProps } from 'react-table';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';

import { SELECTION_COLUMN, TABLE_ROW_CLASS } from '../planner-table';
import TableHeader from './table-header';
import type { RoutesTableTranslations } from '../../routes-planner/translations';

interface ITableRow<T extends object> extends TableRowProps {
	row: Row<T>;
	headerGroups: HeaderGroup<T>[];
	shouldRenderHeader?: boolean;
	translations: RoutesTableTranslations;
	onDragStart?: (event: React.DragEvent<HTMLDivElement>) => void;
	onDragEnd?: (event: React.DragEvent<HTMLDivElement>) => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	isLocked?: boolean;
}

const TableRow = <T extends Record<string, unknown>>({
	row,
	headerGroups,
	shouldRenderHeader,
	translations,
	onDragStart,
	onDragEnd,
	onMouseEnter,
	onMouseLeave,
	isLocked,
	style,
	...restOfProps
}: ITableRow<T>) => {
	const containerRef = useRef<HTMLDivElement>();
	const tableRowRef = useRef<HTMLDivElement>();
	const styling = {};

	const isDraggable = typeof onDragStart === 'function' && !isLocked;

	if (shouldRenderHeader) {
		styling['flexWrap'] = 'wrap';
	}

	const getCell = (cell: Cell<T, any>) => {
		if (cell.column.id === SELECTION_COLUMN) {
			return '';
		} else {
			return cell.render('Cell');
		}
	};

	const dragStartHandler = (event: React.DragEvent<HTMLDivElement>) => {
		if (isDraggable) {
			ReactTooltip.hide(tableRowRef.current);
			onDragStart(event);
		}
	};

	const dragEndHandler = (event: React.DragEvent<HTMLDivElement>) => {
		if (isDraggable) {
			onDragEnd(event);
		}
	};

	const DRAGGABLE_ROW_CLASS = `${TABLE_ROW_CLASS} ${TABLE_ROW_CLASS}-draggable`;
	const rowClassNames = isDraggable ? DRAGGABLE_ROW_CLASS : TABLE_ROW_CLASS;

	const handleMouseEnter = () => {
		if (typeof onMouseEnter === 'function') {
			onMouseEnter();
		}
	};

	const handleMouseLeave = () => {
		if (typeof onMouseLeave === 'function') {
			onMouseLeave();
		}
	};

	return (
		<div
			ref={containerRef}
			className={classNames(rowClassNames, { 'with-header': shouldRenderHeader })}
			data-group-by-value={row.values['run_id']}
			style={{ ...style, ...styling } as CSSProperties}
			{...restOfProps}
		>
			{shouldRenderHeader && <TableHeader headerGroups={headerGroups} inGroup={true} />}
			<div
				ref={tableRowRef}
				className={classNames('table-row-item', { 'row-selected': row.isSelected })}
				draggable={isDraggable}
				onDragStart={dragStartHandler}
				onDragEnd={dragEndHandler}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{row.cells.map(cell => (
					<div {...cell.getCellProps()} className="table-data-cell">
						{getCell(cell)}
					</div>
				))}
			</div>
		</div>
	);
};

export default TableRow;
